import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { loanService } from '../../services/loanService';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/formatters';
import toast from 'react-hot-toast';

const LoanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [repaymentAmount, setRepaymentAmount] = useState('');
  const [showRepaymentModal, setShowRepaymentModal] = useState(false);

  const { data, isLoading } = useQuery(
    ['loan', id],
    () => loanService.getLoanById(id),
    { enabled: !!id }
  );

  const { data: scheduleData } = useQuery(
    ['loan-schedule', id],
    () => loanService.getLoanSchedule(id),
    { enabled: !!id }
  );

  const approveMutation = useMutation(
    () => loanService.approveLoan(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['loan', id]);
        toast.success('Loan approved successfully');
      }
    }
  );

  const disburseMutation = useMutation(
    () => loanService.disburseLoan(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['loan', id]);
        queryClient.invalidateQueries(['loan-schedule', id]);
        toast.success('Loan disbursed successfully');
      }
    }
  );

  const repayMutation = useMutation(
    (amount) => loanService.makeRepayment(id, { amount, payment_method: 'cash' }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['loan', id]);
        queryClient.invalidateQueries(['loan-schedule', id]);
        toast.success('Payment recorded successfully');
        setRepaymentAmount('');
        setShowRepaymentModal(false);
      }
    }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const loan = data?.data;
  const schedule = scheduleData?.data?.schedule || [];
  const summary = scheduleData?.data?.summary || {};

  if (!loan) {
    return (
      <div className="p-6 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800">Loan Not Found</h3>
          <p className="text-red-600 mt-2">The loan you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/loans')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Loans
          </button>
        </div>
      </div>
    );
  }

  const nextPayment = schedule.find(s => s.status === 'pending');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Loan Details</h1>
          <p className="text-gray-600">Loan No: {loan.loan_no}</p>
        </div>
        <div className="flex space-x-2">
          {loan.status === 'pending' && (
            <button
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              {approveMutation.isLoading ? 'Approving...' : 'Approve Loan'}
            </button>
          )}
          {loan.status === 'approved' && (
            <button
              onClick={() => disburseMutation.mutate()}
              disabled={disburseMutation.isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {disburseMutation.isLoading ? 'Disbursing...' : 'Disburse Loan'}
            </button>
          )}
          {loan.status === 'active' && (
            <button
              onClick={() => setShowRepaymentModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Make Payment
            </button>
          )}
        </div>
      </div>

      {/* Loan Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Principal Amount</div>
          <div className="text-2xl font-bold text-blue-600">{formatCurrency(loan.principal_amount)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Interest Rate</div>
          <div className="text-2xl font-bold text-purple-600">{loan.interest_rate}%</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Monthly EMI</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(loan.emi_amount)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Status</div>
          <div className="mt-1">
            <span className={`px-2 py-1 text-sm rounded-full ${getStatusColor(loan.status)}`}>
              {loan.status.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* Member Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Member Information</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-gray-500">Member Name</div>
            <div className="font-medium">{loan.member_name}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Member Code</div>
            <div className="font-medium">{loan.member_code}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Phone</div>
            <div className="font-medium">{loan.phone}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Application Date</div>
            <div className="font-medium">{formatDate(loan.application_date)}</div>
          </div>
        </div>
      </div>

      {/* Loan Progress */}
      {loan.status === 'active' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Loan Progress</h2>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Repayment Progress</span>
              <span>{summary.paid_installments} / {summary.total_installments} installments</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 rounded-full h-2"
                style={{ width: `${(summary.paid_installments / summary.total_installments) * 100}%` }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="text-sm text-gray-600">Total Paid</div>
              <div className="text-xl font-bold text-green-600">{formatCurrency(summary.total_paid)}</div>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <div className="text-sm text-gray-600">Remaining Balance</div>
              <div className="text-xl font-bold text-yellow-600">{formatCurrency(summary.remaining)}</div>
            </div>
          </div>

          {nextPayment && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-gray-600">Next Payment Due</div>
              <div className="font-semibold">
                {formatCurrency(nextPayment.total_due)} on {formatDate(nextPayment.due_date)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Repayment Schedule */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Repayment Schedule</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Principal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Due</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {schedule.map((inst) => (
                <tr key={inst.id} className={inst.status === 'overdue' ? 'bg-red-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {inst.installment_no}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(inst.due_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(inst.principal_due)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(inst.interest_due)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {formatCurrency(inst.total_due)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(inst.paid_amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(inst.status)}`}>
                      {inst.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Repayment Modal */}
      {showRepaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Make Loan Repayment</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Payment Amount</label>
              <input
                type="number"
                value={repaymentAmount}
                onChange={(e) => setRepaymentAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border rounded-lg"
                autoFocus
              />
              {nextPayment && (
                <p className="text-sm text-gray-500 mt-1">
                  Next installment due: {formatCurrency(nextPayment.total_due)}
                </p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRepaymentModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => repayMutation.mutate(parseFloat(repaymentAmount))}
                disabled={!repaymentAmount || repayMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {repayMutation.isLoading ? 'Processing...' : 'Make Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanDetails;